// Auswertung (Spec 4.3 + Max 19.10.: mehr Kennzahlen, Equity-Kurve, nach Jahr/Produkt,
// Timing & Haltedauer, Hinweise). Grundlage: abgerechnete Trades (pnl vorhanden).
import { useMemo, useState } from "react";
import { ComposedChart, Line, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { useTrades } from "./JournalTab";
import { de, ddmm, num, productLabel, signed, strategyLabel, strategyOf, labelOf, CATEGORIES } from "./common";

type Range = "90" | "jahr" | "alles";
const RANGES: { key: Range; label: string }[] = [{ key: "90", label: "90 Tage" }, { key: "jahr", label: "Jahr" }, { key: "alles", label: "Alles" }];
const WD = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

interface Grp { n: number; wins: number; pnl: number; pct: number; fees: number; tax: number; }
const add = (m: Map<string, Grp>, k: string, t: any) => {
  const g = m.get(k) || { n: 0, wins: 0, pnl: 0, pct: 0, fees: 0, tax: 0 };
  g.n++; if (t.pnl > 0) g.wins++; g.pnl += t.pnl; g.pct += num(t.pnl_pct) ?? 0;
  g.fees += (t.fees_entry || 0) + (t.fees_exit || 0); g.tax += t.tax || 0; m.set(k, g);
};
const rate = (g: Grp) => `${Math.round((g.wins / g.n) * 100)} %`;
// Haltedauer in Minuten; Max ist meist intraday raus -> feine Stufen unten, grob oben
const holdMin = (t: any) => t.closed_at ? (new Date(t.closed_at).getTime() - new Date(t.opened_at).getTime()) / 6e4 : null;
const holdBucket = (m: number) => m < 15 ? "unter 15 Min" : m < 60 ? "15–60 Min" : m < 240 ? "1–4 Std" : m < 1440 ? "4–24 Std"
  : m < 7 * 1440 ? "1–7 Tage" : "über 7 Tage";
const HOLD_ORDER = ["unter 15 Min", "15–60 Min", "1–4 Std", "4–24 Std", "1–7 Tage", "über 7 Tage"];
const fmtDur = (m: number) => m < 60 ? `${Math.round(m)} Min` : m < 1440 ? `${de(m / 60, 1)} Std` : `${de(m / 1440, 1)} Tage`;
const hourBucket = (h: number, m: number) => { const t = h * 60 + m;
  return t < 600 ? "vor 10:00" : t < 720 ? "10–12 Uhr" : t < 930 ? "12–15:30" : t < 1050 ? "15:30–17:30" : "nach 17:30"; };
const HOUR_ORDER = ["vor 10:00", "10–12 Uhr", "12–15:30", "15:30–17:30", "nach 17:30"];

function GroupTable({ title, rows, money = true, first = "", order }: { title: string; rows: Map<string, Grp>; money?: boolean; first?: string; order?: string[] }) {
  let list = Array.from(rows.entries());
  list = order ? order.filter(k => rows.has(k)).map(k => [k, rows.get(k)!] as [string, Grp]) : list.sort((a, b) => b[1].pnl - a[1].pnl);
  if (!list.length) return null;
  return (
    <section className="au-sec">
      <h3>{title}</h3>
      <table className="j-table">
        <thead><tr><th>{first}</th><th>n</th><th>Treffer</th><th>{money ? "P&L" : "Ø P&L"}</th></tr></thead>
        <tbody>{list.map(([k, g]) => (
          <tr key={k}><td>{k}</td><td className="mono">{g.n}</td><td className="mono">{rate(g)}</td>
            <td className={`mono ${(money ? g.pnl : g.pct) >= 0 ? "up" : "down"}`}>{money ? signed(g.pnl, 2, " €") : signed(g.pct / g.n)}</td></tr>
        ))}</tbody>
      </table>
    </section>
  );
}

function csvOf(trades: any[]): string {
  const cols = ["eroeffnet", "geschlossen", "symbol", "name", "produkt", "richtung", "kategorie", "strategie", "score",
    "einstieg", "ausstieg", "stueck", "waehrung", "pnl", "pnl_pct", "gebuehren", "steuer", "status", "notiz"];
  const q = (v: any) => { const s = v == null ? "" : String(v); return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const rows = trades.map(t => [String(t.opened_at || "").slice(0, 16), String(t.closed_at || "").slice(0, 16), t.symbol, t.name,
    productLabel(t), t.direction || t.call_put || "", labelOf(CATEGORIES, t.category), t.signal_strategy || t.strategy || "",
    t.sigmund_score ?? "", t.entry_price, t.exit_price ?? "", t.quantity, t.currency, t.pnl ?? "", t.pnl_pct ?? "",
    (t.fees_entry || 0) + (t.fees_exit || 0), t.tax ?? "", t.status, (t.notes || "").replace(/\s+/g, " ")].map(q).join(";"));
  return [cols.join(";"), ...rows].join("\n");
}

export default function AuswertungTab() {
  const { data: all = [], isLoading } = useTrades();
  const [range, setRange] = useState<Range>("alles");
  const [showAllSym, setShowAllSym] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const inRange = (d: string | null) => {
    if (range === "alles") return true;
    if (!d) return false;
    const t = new Date(d);
    if (range === "jahr") return t.getFullYear() === new Date().getFullYear();
    return Date.now() - t.getTime() <= 90 * 864e5;
  };
  const closed = useMemo(() => all.filter((t: any) => t.pnl != null && inRange(t.closed_at || t.opened_at))
    .sort((a: any, b: any) => String(a.closed_at || a.opened_at).localeCompare(String(b.closed_at || b.opened_at))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all, range]);
  const forCsv = all.filter((t: any) => t.status === "open" || inRange(t.closed_at || t.opened_at));

  const copyCsv = async () => {
    const text = csvOf(forCsv);
    let ok = false;
    try { await navigator.clipboard.writeText(text); ok = true; } catch {
      const ta = document.createElement("textarea"); ta.value = text; document.body.appendChild(ta); ta.select();
      ok = document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(ok ? `${forCsv.length} Trades als CSV kopiert` : "Kopieren fehlgeschlagen");
    window.setTimeout(() => setCopied(null), 2500);
  };

  const chips = (
    <div className="sc-chips" style={{ paddingTop: 4 }}>
      {RANGES.map(r => <button key={r.key} className={`sc-chip${range === r.key ? " on" : ""}`} aria-pressed={range === r.key} onClick={() => setRange(r.key)}>{r.label}</button>)}
    </div>
  );
  if (isLoading) return <div className="sc-empty">Lädt …</div>;
  if (!closed.length) return <>{chips}<div className="sc-empty">Keine abgerechneten Trades in diesem Zeitraum.</div>
    <button className="j-add" onClick={copyCsv}>Journal für Claude kopieren (CSV)</button></>;

  // ── Kennzahlen ──
  const wins = closed.filter((t: any) => t.pnl > 0), losses = closed.filter((t: any) => t.pnl < 0);
  const sumW = wins.reduce((s: number, t: any) => s + t.pnl, 0), sumL = Math.abs(losses.reduce((s: number, t: any) => s + t.pnl, 0));
  const avgPct = (l: any[]) => l.length ? l.reduce((s, t) => s + (num(t.pnl_pct) ?? 0), 0) / l.length : null;
  const total = closed.reduce((s: number, t: any) => s + t.pnl, 0);
  let eq = 0, peak = 0, maxDD = 0, cw = 0, cl = 0, bw = 0, bl = 0;
  const curve = closed.map((t: any) => {
    eq += t.pnl; peak = Math.max(peak, eq); maxDD = Math.max(maxDD, peak - eq);
    if (t.pnl > 0) { cw++; cl = 0; bw = Math.max(bw, cw); } else { cl++; cw = 0; bl = Math.max(bl, cl); }
    return { d: String(t.closed_at || t.opened_at).slice(0, 10), equity: +eq.toFixed(2), dd: -(peak - eq).toFixed(2), name: t.name || t.symbol, pnl: t.pnl };
  });
  const streak = cw > 0 ? `${cw} Gewinner` : `${cl} Verlierer`;
  const holds = closed.map(holdMin).filter((d): d is number => d !== null && d >= 0).sort((a, b) => a - b);
  const avgHold = holds.length ? holds.reduce((a, b) => a + b, 0) / holds.length : null;
  const medHold = holds.length ? holds[Math.floor(holds.length / 2)] : null;
  const fees = closed.reduce((s: number, t: any) => s + (t.fees_entry || 0) + (t.fees_exit || 0), 0);
  const tax = closed.reduce((s: number, t: any) => s + (t.tax || 0), 0);
  const currencies = Array.from(new Set(closed.map((t: any) => t.currency || "EUR")));

  // ── Gruppen ──
  const byStrat = new Map<string, Grp>(), bySym = new Map<string, Grp>(), byYear = new Map<string, Grp>(), byProd = new Map<string, Grp>();
  const byWd = new Map<string, Grp>(), byHour = new Map<string, Grp>(), byHold = new Map<string, Grp>();
  closed.forEach((t: any) => {
    add(byStrat, strategyLabel(strategyOf(t)), t);
    add(bySym, t.underlying_symbol || t.name || t.symbol, t);
    add(byYear, String(t.closed_at || t.opened_at).slice(0, 4), t);
    add(byProd, productLabel(t), t);
    const o = new Date(t.opened_at);
    if (!isNaN(o.getTime())) { add(byWd, WD[o.getDay()], t); add(byHour, hourBucket(o.getHours(), o.getMinutes()), t); }
    const h = holdMin(t); if (h !== null && h >= 0) add(byHold, holdBucket(h), t);
  });
  const symList = new Map(Array.from(bySym.entries()).sort((a, b) => b[1].pnl - a[1].pnl).slice(0, showAllSym ? undefined : 10));

  // ── Hinweise (nur aus den Daten, nur wenn genug Trades) ──
  const hints: string[] = [];
  const share = (g?: Grp) => g && g.n >= 3 ? (g.n - g.wins) / g.n : 0;
  if (share(byWd.get("Mo")) > 0.6) hints.push(`Montag: ${Math.round(share(byWd.get("Mo")) * 100)} % der Trades sind Verlierer.`);
  if (share(byWd.get("Fr")) > 0.6) hints.push(`Freitag: ${Math.round(share(byWd.get("Fr")) * 100)} % der Trades sind Verlierer.`);
  if (cw >= 5) hints.push(`Aktuell ${cw} Gewinner in Folge.`);
  if (cl >= 4) hints.push(`Aktuell ${cl} Verlierer in Folge.`);
  const bestHour = Array.from(byHour.entries()).filter(([, g]) => g.n >= 3).sort((a, b) => b[1].wins / b[1].n - a[1].wins / a[1].n)[0];
  if (bestHour) hints.push(`Beste Einstiegszeit: ${bestHour[0]} (${rate(bestHour[1])} Treffer bei ${bestHour[1].n} Trades).`);
  const bestHold = Array.from(byHold.entries()).filter(([, g]) => g.n >= 3).sort((a, b) => b[1].pnl - a[1].pnl)[0];
  if (bestHold) hints.push(`Ertragreichste Haltedauer: ${bestHold[0]} (${signed(bestHold[1].pnl, 2, " €")}).`);

  const kpi = (label: string, val: string, cls = "") => <div className="au-kpi"><span>{label}</span><strong className={`mono ${cls}`}>{val}</strong></div>;
  const line = (label: string, val: string, cls = "") => <div className="au-line"><span>{label}</span><span className={`mono ${cls}`}>{val}</span></div>;
  const ap = avgPct(wins), al = avgPct(losses);

  return (
    <>
      {chips}
      <div className="au-kpis">
        {kpi("Trades (abgerechnet)", String(closed.length))}
        {kpi("Trefferquote", `${Math.round((wins.length / closed.length) * 100)} %`)}
        {kpi("Ø Gewinner", ap === null ? "–" : signed(ap), "up")}
        {kpi("Ø Verlierer", al === null ? "–" : signed(al), "down")}
      </div>

      <section className="au-sec">
        <h3>Kennzahlen</h3>
        {line("Gesamt-P&L", signed(total, 2, " €"), total >= 0 ? "up" : "down")}
        {line("Profit Factor", sumL > 0 ? de(sumW / sumL) : "–")}
        {line("Erwartungswert je Trade", signed(total / closed.length, 2, " €"))}
        {line("Max. Drawdown", maxDD > 0 ? `−${de(maxDD)} €` : "0 €")}
        {line("Beste / schlimmste Serie", `${bw} Gewinner / ${bl} Verlierer`)}
        {line("Aktuelle Serie", streak)}
        {line("Haltedauer Median / Ø", medHold === null || avgHold === null ? "–" : `${fmtDur(medHold)} / ${fmtDur(avgHold)}`)}
        {line("Gebühren / Steuern", `${de(fees)} € / ${de(tax)} €`)}
        {currencies.length > 1 && <p className="au-note">Beträge in {currencies.join(", ")} sind ohne Umrechnung addiert.</p>}
      </section>

      <section className="au-sec">
        <h3>Equity-Kurve</h3>
        <p className="au-note">Linie: kumulierter P&L in € · Fläche: Abstand zum bisherigen Hoch (Drawdown)</p>
        <ResponsiveContainer width="100%" height={180}>
          <ComposedChart data={curve} margin={{ top: 6, right: 4, bottom: 0, left: 0 }}>
            <XAxis dataKey="d" tick={{ fontSize: 10, fill: "var(--text-faint)" }} tickLine={false} axisLine={{ stroke: "var(--border)" }}
              minTickGap={48} tickFormatter={(v: string) => ddmm(v) + v.slice(2, 4)} />
            <YAxis tick={{ fontSize: 10, fill: "var(--text-faint)" }} tickLine={false} axisLine={false} width={48}
              tickFormatter={(v: number) => Math.round(v).toLocaleString("de-DE")} />
            <ReferenceLine y={0} stroke="var(--border-strong)" />
            <Tooltip cursor={{ stroke: "var(--border-strong)" }}
              contentStyle={{ background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 6, fontSize: 12, color: "var(--text)" }}
              labelFormatter={(l: any, p: any) => `${ddmm(String(l))}${String(l).slice(0, 4)} · ${p?.[0]?.payload?.name ?? ""}`}
              formatter={(v: any, k: any) => [`${de(v)} €`, k === "equity" ? "kumuliert" : "Drawdown"]} />
            <Area dataKey="dd" type="stepAfter" stroke="none" fill="var(--red-soft)" isAnimationActive={false} />
            <Line dataKey="equity" type="monotone" stroke="var(--bar)" strokeWidth={2} dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </section>

      <GroupTable title="Nach Strategie" rows={byStrat} money={false} first="Strategie" />
      <GroupTable title="Nach Symbol" rows={symList} first="Symbol" />
      {bySym.size > 10 && <button className="au-more" onClick={() => setShowAllSym(s => !s)}>{showAllSym ? "Weniger" : `Alle ${bySym.size} anzeigen`}</button>}
      <GroupTable title="Nach Jahr" rows={byYear} first="Jahr" order={Array.from(byYear.keys()).sort().reverse()} />
      <GroupTable title="Nach Produkt" rows={byProd} first="Produkt" />
      <GroupTable title="Wochentag (Einstieg)" rows={byWd} first="Tag" order={["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"]} />
      <GroupTable title="Uhrzeit (Einstieg)" rows={byHour} first="Zeit" order={HOUR_ORDER} />
      <GroupTable title="Haltedauer" rows={byHold} first="Dauer" order={HOLD_ORDER} />

      {hints.length > 0 && (
        <section className="au-sec"><h3>Hinweise</h3>{hints.map(h => <p key={h} className="au-hint">{h}</p>)}</section>
      )}

      {copied && <div className="sc-toast" role="status"><span>{copied}</span></div>}
      <button className="j-add" onClick={copyCsv}>Journal für Claude kopieren (CSV)</button>
    </>
  );
}
